import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import imgUrl from '../assets/images/imgUrl';
import { useNavigate, useSearchParams } from "react-router";
import { apiServices } from '../services/apiServices';
import Swal from 'sweetalert2';

const SetupPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const email = searchParams.get('email');
  
  const [passwordData, setPasswordData] = useState({
    password: '',
    confirmPassword: '',
    otp: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect to login if no email is provided
  useEffect(() => {
    if (!email) {
      navigate('/login');
    }
  }, [email, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password
    if (!passwordData.password.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Password Required',
        text: 'Please enter a password',
        confirmButtonColor: '#3A8139'
      });
      return;
    }

    // Validate confirm password
    if (!passwordData.confirmPassword.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'Confirm Password Required',
        text: 'Please confirm your password',
        confirmButtonColor: '#3A8139'
      });
      return;
    }

    // Validate passwords match
    if (passwordData.password !== passwordData.confirmPassword) {
      Swal.fire({
        icon: 'error',
        title: 'Passwords Do Not Match',
        text: 'Please make sure your passwords match',
        confirmButtonColor: '#3A8139'
      });
      return;
    }

    // Validate otp
    if (!passwordData.otp.trim()) {
      Swal.fire({
        icon: 'error',
        title: 'OTP Required',
        text: 'Please enter the OTP',
        confirmButtonColor: '#3A8139'
      });
      return;
    }

    // Validate password strength
    if (passwordData.password.length < 8) {
      Swal.fire({
        icon: 'error',
        title: 'Password Too Short',
        text: 'Password must be at least 8 characters long',
        confirmButtonColor: '#3A8139'
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Make API call to validate otp and set password
      const response = await apiServices.postAPI('validate-token', {
        email: email,
        otp: passwordData.otp,
        password: passwordData.password,
        confirmPassword: passwordData.confirmPassword
      });

      if (response && response.status >= 200 && response.status < 300) {
        Swal.fire({
          icon: 'success',
          title: 'Password Set Successfully!',
          text: 'Your password has been set. You can now login with your new password.',
          confirmButtonColor: '#3A8139'
        }).then(() => {
          navigate('/login');
        });
      } else {
        throw new Error(response?.data?.message || 'Failed to set password');
      }
    } catch (error) {
      console.error('Error setting password:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to set password. Please try again.',
        confirmButtonColor: '#3A8139'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
       className="LoginSection"
    >
      <Paper
        elevation={5}
        sx={{
          p: 4,
          width: 360,
          textAlign: 'center',
          borderRadius: 2
        }}
        className='UserSubSection'
      >
       <Box sx={{ mb: 2 }}>
          <img src={imgUrl.peaceMovImg} alt="Logo" width="100" /> {/* Replace with your logo path */}
        </Box>
        <Typography variant="h6" fontWeight="400" mb={0} className='loginHeading'>
          Setup Password
        </Typography>
         <Typography variant="body2" color="text.secondary" mb={4}>
                  Setup Password for peacekeeper only
                </Typography>

         {/* Display the email address */}
         {email && (
           <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
             <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
               Setting up password for:
             </Typography>
             <Typography variant="body1" fontWeight="500" color="primary.main">
               {email}
             </Typography>
           </Box>
         )}

        <form onSubmit={handleSubmit} autoComplete="off">
          {/* Hidden field to prevent autofill */}
          <input type="text" style={{ display: 'none' }} autoComplete="username" />
          
         <Typography sx={{textAlign:"left"}}>OTP</Typography>
         {/* OTP Input */}
         <TextField
           fullWidth
           variant="outlined"
           placeholder='Please enter OTP'
           type="text"
           sx={{ mb: 2 }}
           name="otp"
           value={passwordData.otp}
           onChange={handleInputChange}
         />
          <Box sx={{ textAlign: 'left', mb: 2}}>
            <Typography variant="body2">Enter Password</Typography>
            <TextField
              fullWidth
              name="password"
              placeholder="Enter password"
              type={showPassword ? 'text' : 'password'}
              variant="outlined"
              size="small"
              margin="dense"
              value={passwordData.password}
              onChange={handleInputChange}
              autoComplete="new-password"
              inputProps={{
                autoComplete: 'new-password',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: 'false'
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Box sx={{ textAlign: 'left', mb: 2 }}>
            <Typography variant="body2">Confirm Password</Typography>
            <TextField
              fullWidth
              name="confirmPassword"
              placeholder="Confirm password"
              type={showConfirm ? 'text' : 'password'}
              variant="outlined"
              size="small"
              margin="dense"
              value={passwordData.confirmPassword}
              onChange={handleInputChange}
              autoComplete="new-password"
              inputProps={{
                autoComplete: 'new-password',
                autoCorrect: 'off',
                autoCapitalize: 'off',
                spellCheck: 'false'
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </Box>

          <Button
                    fullWidth
                    variant="contained"
                    disabled={isSubmitting}
                    type="submit"
                    sx={{
                      mt: 2,
                      background: 'linear-gradient(to right, #214F7D, #3A8139)',
                      color: 'white',
                      borderRadius:"50px"
                    }}
                  >
                    {isSubmitting ? 'Setting Password...' : 'Submit'}
                  </Button>
          
          {/* Back Button */}
          <Button
            fullWidth
            variant="outlined"
            disabled={isSubmitting}
            sx={{
              mt: 1,
              borderColor: '#214F7D',
              color: '#214F7D',
              borderRadius:"50px"
            }}
            onClick={() => navigate("/login")}
          >
            Back to Login
          </Button>
          
        </form>
      </Paper>
    </Box>
  );
};

export default SetupPassword;
